import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, ArrowLeft, Camera, Save, Upload } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { API_BASE_URL } from '../config';
import { processImageFile, IMAGE_CONFIG } from '../utils/imageCompression';
import secureStorage from '../utils/secureStorage';
import useImageDrop from '../utils/useImageDrop';

const API = `${API_BASE_URL}/api`;

// ─── Shared HIRA risk score algorithm ───────────────────────────────
// Straight sum:
//   Impact points  = frequency + fatalities + injuries + evacuation +
//                    property_damage + critical_infrastructure +
//                    environmental_damage + business_financial_impact +
//                    psychosocial_impact
//                    (each field contributes its numeric level as points)
//   Trend points   = 1 point per "Yes" across 8 change checkboxes
//                    (4 frequency + 4 vulnerability)
//   Total          = Impact + Trend
// Levels: Critical ≥ 32 · High 20-31 · Medium 10-19 · Low < 10
export const calculateHIRARiskScore = (entry = {}) => {
  const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const impactFields = [
    'frequency', 'fatalities', 'injuries', 'evacuation', 'property_damage',
    'critical_infrastructure', 'environmental_damage',
    'business_financial_impact', 'psychosocial_impact',
  ];
  const impactPoints = impactFields.reduce((sum, k) => sum + num(entry[k]), 0);
  const changeFreq = Array.isArray(entry.change_in_frequency) ? entry.change_in_frequency : [];
  const changeVuln = Array.isArray(entry.change_in_vulnerability) ? entry.change_in_vulnerability : [];
  const yesCount = [...changeFreq, ...changeVuln].filter((v) => v === true).length;
  const trend = yesCount * 1;
  const total = impactPoints + trend;
  // Keep `base` in the return shape for backwards compat with existing UI
  return { base: impactPoints, trend, yesCount, total };
};

export const getHIRARiskLevel = (entry = {}) => {
  const { total } = calculateHIRARiskScore(entry);
  if (total >= 32) return { level: 'Critical', color: 'bg-red-500/20 text-red-300 border-red-500/30', score: total };
  if (total >= 20) return { level: 'High', color: 'bg-orange-500/20 text-orange-300 border-orange-500/30', score: total };
  if (total >= 10) return { level: 'Medium', color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30', score: total };
  return { level: 'Low', color: 'bg-green-500/20 text-green-300 border-green-500/30', score: total };
};

// Memoized RadioSection for HIRA risk assessment levels
const RadioSection = React.memo(({ title, levels, value, onChange, required = true, scrollRef }) => {
  const handleChange = (val) => {
    if (scrollRef) {
      scrollRef.current = window.pageYOffset || document.documentElement.scrollTop;
    }
    onChange(parseInt(val));
  };

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader>
        <CardTitle className="text-orange-500 flex items-center">
          {title}
          {required && <span className="text-red-400 ml-1">*</span>}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <RadioGroup value={value.toString()} onValueChange={handleChange}>
          {levels.map((level) => (
            <div
              key={level.level}
              className="flex items-start space-x-3 py-2"
              onMouseDown={() => {
                if (scrollRef) {
                  scrollRef.current = window.pageYOffset || document.documentElement.scrollTop;
                }
              }}
            >
              <RadioGroupItem
                value={level.level.toString()}
                id={`${title}-${level.level}`}
              />
              <div className="flex-1">
                <Label
                  htmlFor={`${title}-${level.level}`}
                  className="text-white font-medium cursor-pointer"
                >
                  Level {level.level} - {level.label}
                </Label>
                <p className="text-sm text-gray-400 mt-1">{level.description}</p>
              </div>
            </div>
          ))}
        </RadioGroup>
      </CardContent>
    </Card>
  );
});

const HIRAForm = ({ onBack, onSave, editingEntry = null }) => {
  const formRef = useRef(null);
  const scrollPositionRef = useRef(0);
  const isRestoringScrollRef = useRef(false);
  
  const [formData, setFormData] = useState(() => {
    if (editingEntry) {
      let formattedLat = '';
      let formattedLng = '';
      if (editingEntry.latitude !== null && editingEntry.latitude !== undefined) {
        const latNum = parseFloat(editingEntry.latitude);
        formattedLat = !isNaN(latNum) ? latNum.toFixed(4) : editingEntry.latitude.toString();
      }
      if (editingEntry.longitude !== null && editingEntry.longitude !== undefined) {
        const lngNum = parseFloat(editingEntry.longitude);
        formattedLng = !isNaN(lngNum) ? lngNum.toFixed(4) : editingEntry.longitude.toString();
      }
      return {
        name: editingEntry.name || '',
        description: editingEntry.description || '',
        notes: editingEntry.notes || '',
        disaster_type: editingEntry.disaster_type || '',
        latitude: formattedLat,
        longitude: formattedLng,
        frequency: editingEntry.frequency || 1,
        fatalities: editingEntry.fatalities || 0,
        injuries: editingEntry.injuries || 0,
        evacuation: editingEntry.evacuation || 0,
        property_damage: editingEntry.property_damage || 0,
        critical_infrastructure: editingEntry.critical_infrastructure || 0,
        environmental_damage: editingEntry.environmental_damage || 0,
        business_financial_impact: editingEntry.business_financial_impact || 0,
        psychosocial_impact: editingEntry.psychosocial_impact || 0,
        change_in_frequency: editingEntry.change_in_frequency || [false, false, false, false],
        change_in_vulnerability: (() => {
          const v = editingEntry.change_in_vulnerability || [];
          // Pad legacy 3-item records to 4 items for the new Q1/Q2 split
          const padded = [...v];
          while (padded.length < 4) padded.push(false);
          return padded.slice(0, 4);
        })(),
        hazard_image: editingEntry.hazard_image || null
      };
    }
    return {
      name: '',
      description: '',
      notes: '',
      disaster_type: '',
      latitude: '',
      longitude: '',
      frequency: 0,
      fatalities: 0,
      injuries: 0,
      evacuation: 0,
      property_damage: 0,
      critical_infrastructure: 0,
      environmental_damage: 0,
      business_financial_impact: 0,
      psychosocial_impact: 0,
      change_in_frequency: [false, false, false, false],
      change_in_vulnerability: [false, false, false, false],
      hazard_image: null
    };
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Validation error states
  const [validationErrors, setValidationErrors] = useState({
    latitude: '',
    longitude: ''
  });

  // Track scroll position continuously
  useEffect(() => {
    const handleScroll = () => {
      if (!isRestoringScrollRef.current) {
        scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Restore scroll position after any state change
  useEffect(() => {
    if (scrollPositionRef.current > 0) {
      isRestoringScrollRef.current = true;
      requestAnimationFrame(() => {
        window.scrollTo({
          top: scrollPositionRef.current,
          behavior: 'instant'
        });
        // Small delay to ensure scroll completes before allowing tracking again
        setTimeout(() => {
          isRestoringScrollRef.current = false;
        }, 50);
      });
    }
  });

  // Man-made list ends where natural list begins. The natural list starts at
  // 'Earthquake' (first natural disaster). We split dynamically so future edits
  // don't need to touch the render code below.
  const disasterTypes = [
    // ── Man-Made Disasters ──
    'Structure fire',
    'High-rise fire',
    'Industrial fire',
    'Warehouse fire',
    'Refinery fire',
    'Chemical plant fire',
    'Explosion',
    'Industrial explosion',
    'Gas explosion',
    'Grain elevator explosion',
    'Mine explosion',
    'Mine collapse',
    'Pipeline explosion',
    'Hazardous-material spill',
    'Chemical spill',
    'Toxic chemical release',
    'Chlorine release',
    'Ammonia release',
    'Fuel spill',
    'Oil spill',
    'Pipeline rupture',
    'Natural-gas pipeline failure',
    'Radioactive-material release',
    'Nuclear power plant accident',
    'Radiation emergency',
    'Biological-material release',
    'Laboratory accident',
    'Transportation accident',
    'Major highway collision',
    'Multi-vehicle collision',
    'Bus crash',
    'Train derailment',
    'Passenger-train collision',
    'Freight-train accident',
    'Aircraft crash',
    'Helicopter crash',
    'Airport emergency',
    'Marine vessel collision',
    'Ship sinking',
    'Ferry accident',
    'Port accident',
    'Bridge collapse',
    'Building collapse',
    'Structural failure',
    'Crane collapse',
    'Dam failure',
    'Dike or levee failure',
    'Tunnel collapse',
    'Construction-site disaster',
    'Power-grid failure',
    'Widespread electrical blackout',
    'Telecommunications failure',
    'Internet outage',
    'Cellular-network failure',
    'Water-treatment failure',
    'Drinking-water contamination',
    'Wastewater-treatment failure',
    'Sewer-system failure',
    'Natural-gas service interruption',
    'Fuel shortage',
    'Supply-chain disruption',
    'Food contamination',
    'Food-processing emergency',
    'Cyberattack',
    'Ransomware attack',
    'Critical-infrastructure cyberattack',
    'Data-centre failure',
    'Emergency-communications failure',
    'Navigation-system/GPS disruption',
    'Industrial-control-system failure',
    'Crowd control',
    'Stadium emergency',
    'Mass-casualty incident',
    'Public-event emergency',
    'Civil disturbance',
    'Political uprise',
    'Riot',
    'Hostage situation',
    'Active assailant',
    'Active shooter',
    'Bomb threat',
    'Bombing',
    'Terrorist attack',
    'Sabotage',
    'Arson',
    'Vehicle-ramming attack',
    'Chemical attack',
    'Biological attack',
    'Radiological attack',
    'Nuclear attack',
    'Drone-related incident',
    'Infrastructure sabotage',
    'Prison riot',
    'Inmate escaped',
    'Barricaded inmate',
    'Gang fight',
    'Lockdown',
    'Loss of control',
    'War or armed conflict',
    'Civil war',
    'Military attack',
    'Mass displacement of population',
    'Refugee emergency',
    'Humanitarian crisis',
    // ── Natural Disasters ──
    'Earthquake',
    'Tsunami',
    'Volcanic eruption',
    'Landslide',
    'Rockslide',
    'Mudslide',
    'Avalanche',
    'Sinkhole',
    'Flood',
    'Flash flood',
    'River flooding',
    'Coastal flooding',
    'Storm surge',
    'Ice-jam flooding',
    'Hurricane',
    'Tornado',
    'Severe thunderstorm',
    'Lightning storm',
    'Hailstorm',
    'Windstorm',
    'Blizzard',
    'Heavy snowfall',
    'Ice storm',
    'Freezing rain',
    'Extreme cold',
    'Extreme heat',
    'Heat wave',
    'Drought',
    'Wildfire',
    'Forest fire',
    'Grass fire',
    'Natural gas release from geological activity',
    'Coastal erosion',
    'Severe erosion',
    'Meteor or asteroid impact',
    'Geomagnetic/solar storm',
    'Epidemic',
    'Pandemic',
    'Infectious disease outbreak',
    'Animal disease outbreak',
    'Agricultural pest infestation',
    'Harmful algal bloom',
    'Waterborne disease outbreak',
  ];
  // Index at which the natural-disaster subset begins (first item after the
  // last man-made entry). Used by the render below to split the dropdown into
  // two grouped sections.
  const NATURAL_START_INDEX = disasterTypes.indexOf('Earthquake');

  const frequencyLevels = [
    { level: 0, label: 'Rare', description: 'Less than a 1% chance of occurrence in any year. Hazards with return periods >100 years.' },
    { level: 1, label: 'Very Unlikely', description: 'Between a 1 - 2% chance of occurrence in any year. Occurs every 50 – 100 years and includes hazards that have not occurred but are reported to be more likely to occur in the near future.' },
    { level: 2, label: 'Unlikely', description: 'Between a 2 – 10% chance of occurrence in any year. Occurs every 20 – 50 years.' },
    { level: 3, label: 'Probable', description: 'Between a 10 – 50% chance of occurrence in any year. Occurs every 5 – 20 years.' },
    { level: 4, label: 'Likely', description: 'Between a 50 – 100% chance of occurrence in any year. Occurs > 5 years.' },
    { level: 5, label: 'Almost Certain', description: '100% chance of occurrence in any year. The hazard occurs annually.' }
  ];

  const fatalityLevels = [
    { level: 0, label: 'None', description: 'Not likely to result in fatalities within the community.' },
    { level: 1, label: 'Minor', description: 'Could result in fewer than five fatalities within the community.' },
    { level: 2, label: 'Moderate', description: 'Could result in 5 – 10 fatalities within the community.' },
    { level: 3, label: 'Severe', description: 'Could result in 10 – 50 fatalities within the community.' },
    { level: 4, label: 'Catastrophic', description: 'Could result in +50 fatalities within the community.' }
  ];

  const injuryLevels = [
    { level: 0, label: 'None', description: 'Not likely to result in injuries within the community.' },
    { level: 1, label: 'Minor', description: 'Could injure fewer than 25 people within the community.' },
    { level: 2, label: 'Moderate', description: 'Could injure 25 – 100 people within the community.' },
    { level: 3, label: 'Severe', description: 'Could injure +100 people within the community.' }
  ];

  const evacuationLevels = [
    { level: 0, label: 'None', description: 'Not likely to result in an evacuation shelter-in-place orders, or people stranded.' },
    { level: 1, label: 'Minor', description: 'Could result in fewer than 100 people being evacuated, sheltered-in-place or stranded.' },
    { level: 2, label: 'Moderate', description: 'Could result in 100 - 500 people being evacuated, sheltered-in-place or stranded.' },
    { level: 3, label: 'Severe', description: 'Could result in more than 500 people being evacuated, sheltered-in-place or stranded.' }
  ];

  const propertyLevels = [
    { level: 0, label: 'None', description: 'Not likely to result in property damage within the community.' },
    { level: 1, label: 'Minor', description: 'Could cause minor and mostly cosmetic damage.' },
    { level: 2, label: 'Moderate', description: 'Localized severe damage (a few buildings destroyed).' },
    { level: 3, label: 'Severe', description: 'Widespread severe damage (many buildings destroyed).' }
  ];

  const infrastructureLevels = [
    { level: 0, label: 'None', description: 'Not likely to disrupt critical infrastructure services.' },
    { level: 1, label: 'Minor', description: 'Could disrupt 1 critical infrastructure service.' },
    { level: 2, label: 'Moderate', description: 'Could disrupt 2 – 3 critical infrastructure services.' },
    { level: 3, label: 'Severe', description: 'Could disrupt more than 3 critical infrastructure services.' }
  ];

  const environmentalLevels = [
    { level: 0, label: 'None', description: 'Not likely to result in environmental damage.' },
    { level: 1, label: 'Minor', description: 'Could cause localized and reversible damage. Quick clean up possible.' },
    { level: 2, label: 'Moderate', description: 'Could cause major but reversible damage. Full clean up difficult.' },
    { level: 3, label: 'Severe', description: 'Could cause severe and irreversible environmental damage. Full clean up not possible.' }
  ];

  const businessLevels = [
    { level: 0, label: 'None', description: 'Not likely to disrupt business / financial activities.' },
    { level: 1, label: 'Minor', description: 'Could cause local and reversible disruption to business / financial / industry activities.' },
    { level: 2, label: 'Moderate', description: 'Could result in losses for an business / financial / industry. Limited clean up' },
    { level: 3, label: 'Severe', description: 'Could cause severe and irreversible environmental damage. Full clean up not possible.' }
  ];

  const psychosocialLevels = [
    { level: 0, label: 'None', description: 'Not likely to result in significant psychosocial impacts.' },
    { level: 1, label: 'Minor', description: 'Short-term, low-intensity disruption in the well-being of residents that does not lead to long-term psychological trauma or permanent breakdown of community.' },
    { level: 2, label: 'Moderate', description: 'Significant psychosocial impacts including limited panic, hoarding, self-evacuation and long-term psychosocial impacts.' },
    { level: 3, label: 'Severe', description: 'Widespread psychosocial impacts, e.g. mass panic, widespread hoarding and self-evacuation and long-term psychological impacts.' }
  ];

  const frequencyChangeQuestions = [
    'Are the number of non-emergency occurrences of the hazard increasing?',
    'Is human activity (e.g. population growth, change of drainage patterns) likely to lead to more interaction with the hazard or an increase in frequency?',
    'Are there an environmental reasons (e.g. climate change) why the frequency of this hazard may increase?',
    'Are human factors such as business, financial, international practices more likely to increase the risk?'
  ];

  const vulnerabilityChangeQuestions = [
    'Is a large number of the population vulnerable?',
    'Is the number of people vulnerable to this hazard increasing?',
    'Does critical infrastructure reliance or a "just-on-time" delivery system (e.g. stores not keeping a supply of food and relying on frequent shipments) make the population more vulnerable?',
    'Are response agencies not practiced or prepared to respond to this hazard?'
  ];

  // Validation helper functions
  const validateLatitudeField = (lat) => {
    // Required field validation
    if (lat === '' || lat === null || lat === undefined) {
      return 'Latitude is required';
    }
    
    const latStr = lat.toString().trim();
    
    // Check if it's a valid number format (allows integers and decimals up to 4 places)
    const latitudeRegex = /^-?\d{1,2}(\.\d{1,4})?$/;
    
    // First check if it's a valid number
    const latNum = parseFloat(latStr);
    if (isNaN(latNum)) {
      return 'Please enter a valid number for latitude (e.g., 47.1234)';
    }
    
    // Check format - must be like 47.1234 (1-2 digits before decimal, up to 4 after)
    if (!latitudeRegex.test(latStr) && latStr !== '90' && latStr !== '-90') {
      return 'Format error: Enter latitude like 47.1234 (max 4 decimal places)';
    }
    
    // Range validation: -90.0000 to 90.0000
    if (latNum < -90 || latNum > 90) {
      return 'Range error: Latitude must be between -90.0000 and 90.0000';
    }
    
    return '';
  };

  const validateLongitudeField = (lng) => {
    // Required field validation
    if (lng === '' || lng === null || lng === undefined) {
      return 'Longitude is required';
    }
    
    const lngStr = lng.toString().trim();
    
    // Check if it's a valid number format (allows integers and decimals up to 4 places)
    const longitudeRegex = /^-?\d{1,3}(\.\d{1,4})?$/;
    
    // First check if it's a valid number
    const lngNum = parseFloat(lngStr);
    if (isNaN(lngNum)) {
      return 'Please enter a valid number for longitude (e.g., 97.1234)';
    }
    
    // Check format - must be like 97.1234 (1-3 digits before decimal, up to 4 after)
    if (!longitudeRegex.test(lngStr) && lngStr !== '180' && lngStr !== '-180') {
      return 'Format error: Enter longitude like 97.1234 (max 4 decimal places)';
    }
    
    // Range validation: -180.0000 to 180.0000
    if (lngNum < -180 || lngNum > 180) {
      return 'Range error: Longitude must be between -180.0000 and 180.0000';
    }
    
    return '';
  };

  const handleLatitudeChange = (e) => {
    const lat = e.target.value;
    setFormData(prev => ({ ...prev, latitude: lat }));
    
    // Real-time validation
    const error = validateLatitudeField(lat);
    setValidationErrors(prev => ({ ...prev, latitude: error }));
  };

  // Format latitude to 4 decimal places on blur
  const handleLatitudeBlur = (e) => {
    const lat = e.target.value.trim();
    if (lat !== '' && !isNaN(parseFloat(lat))) {
      const latNum = parseFloat(lat);
      if (latNum < -90 || latNum > 90) {
        alert('Latitude must be between -90.0000 and 90.0000');
        return;
      }
      const parts = lat.split('.');
      if (parts.length > 1 && parts[1].length > 4) {
        alert('Latitude must have up to 4 decimal places (e.g., 47.0000)');
        return;
      }
      // Format to exactly 4 decimal places without rounding
      const formatted = latNum.toFixed(4);
      setFormData(prev => ({ ...prev, latitude: formatted }));
      // Re-validate with formatted value
      const error = validateLatitudeField(formatted);
      setValidationErrors(prev => ({ ...prev, latitude: error }));
    } else if (lat !== '') {
      alert('Latitude must be a valid number (e.g., 47.0000)');
    }
  };

  const handleLongitudeChange = (e) => {
    const lng = e.target.value;
    setFormData(prev => ({ ...prev, longitude: lng }));
    
    // Real-time validation
    const error = validateLongitudeField(lng);
    setValidationErrors(prev => ({ ...prev, longitude: error }));
  };

  // Format longitude to 4 decimal places on blur
  const handleLongitudeBlur = (e) => {
    const lng = e.target.value.trim();
    if (lng !== '' && !isNaN(parseFloat(lng))) {
      const lngNum = parseFloat(lng);
      if (lngNum < -180 || lngNum > 180) {
        alert('Longitude must be between -180.0000 and 180.0000');
        return;
      }
      const parts = lng.split('.');
      if (parts.length > 1 && parts[1].length > 4) {
        alert('Longitude must have up to 4 decimal places (e.g., -97.0000)');
        return;
      }
      // Format to exactly 4 decimal places without rounding
      const formatted = lngNum.toFixed(4);
      setFormData(prev => ({ ...prev, longitude: formatted }));
      // Re-validate with formatted value
      const error = validateLongitudeField(formatted);
      setValidationErrors(prev => ({ ...prev, longitude: error }));
    } else if (lng !== '') {
      alert('Longitude must be a valid number (e.g., -97.0000)');
    }
  };

  useEffect(() => {
    if (editingEntry) {
      // Format latitude and longitude to 4 decimal places when loading
      let formattedLat = '';
      let formattedLng = '';
      
      if (editingEntry.latitude !== null && editingEntry.latitude !== undefined) {
        const latNum = parseFloat(editingEntry.latitude);
        formattedLat = !isNaN(latNum) ? latNum.toFixed(4) : editingEntry.latitude.toString();
      }
      
      if (editingEntry.longitude !== null && editingEntry.longitude !== undefined) {
        const lngNum = parseFloat(editingEntry.longitude);
        formattedLng = !isNaN(lngNum) ? lngNum.toFixed(4) : editingEntry.longitude.toString();
      }
      
      setFormData({
        name: editingEntry.name || '',
        description: editingEntry.description || '',
        notes: editingEntry.notes || '',
        disaster_type: editingEntry.disaster_type || '',
        latitude: formattedLat,
        longitude: formattedLng,
        frequency: editingEntry.frequency || 1,
        fatalities: editingEntry.fatalities || 0,
        injuries: editingEntry.injuries || 0,
        evacuation: editingEntry.evacuation || 0,
        property_damage: editingEntry.property_damage || 0,
        critical_infrastructure: editingEntry.critical_infrastructure || 0,
        environmental_damage: editingEntry.environmental_damage || 0,
        business_financial_impact: editingEntry.business_financial_impact || 0,
        psychosocial_impact: editingEntry.psychosocial_impact || 0,
        change_in_frequency: editingEntry.change_in_frequency || [false, false, false, false],
        change_in_vulnerability: (() => {
          const v = editingEntry.change_in_vulnerability || [];
          const padded = [...v];
          while (padded.length < 4) padded.push(false);
          return padded.slice(0, 4);
        })(),
        hazard_image: editingEntry.hazard_image || null
      });
      if (editingEntry.hazard_image) {
        setImagePreview(editingEntry.hazard_image);
      }
    }
  }, [editingEntry]);

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Use image compression utility for hazard images (1200x800, max 500KB)
      const result = await processImageFile(file, 'hazard');
      if (result.success) {
        setImagePreview(result.data);
        setFormData(prev => ({ ...prev, hazard_image: result.data }));
      } else {
        alert(result.error || 'Failed to process image');
      }
    }
  };

  // Drag-and-drop wiring — delegates to existing handleImageUpload
  const { dropProps: hazardDropProps, isDragging: isHazardDragging } = useImageDrop({
    onFile: (file) => handleImageUpload({ target: { files: [file] } })
  });

  const handleCameraCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = document.createElement('video');
      video.srcObject = stream;
      video.play();
      
      video.addEventListener('loadedmetadata', () => {
        const canvas = document.createElement('canvas');
        // Limit camera capture to hazard image size (1200x800)
        const config = IMAGE_CONFIG.hazard;
        const scale = Math.min(config.maxWidth / video.videoWidth, config.maxHeight / video.videoHeight, 1);
        canvas.width = video.videoWidth * scale;
        canvas.height = video.videoHeight * scale;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        const imageData = canvas.toDataURL('image/jpeg', config.quality);
        setImagePreview(imageData);
        setFormData(prev => ({ ...prev, hazard_image: imageData }));
        
        stream.getTracks().forEach(track => track.stop());
      });
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Unable to access camera. Please use file upload instead.');
    }
  };

  const handleSubmitLocal = async (e) => {
    e.preventDefault();
    
    // Validate required latitude
    if (!formData.latitude || formData.latitude.toString().trim() === '') {
      alert('Please enter a Latitude value. This field is required.');
      return;
    }
    
    // Validate required longitude
    if (!formData.longitude || formData.longitude.toString().trim() === '') {
      alert('Please enter a Longitude value. This field is required.');
      return;
    }
    
    // Validate latitude format (e.g., 47.1234)
    const latStr = formData.latitude.toString().trim();
    const latitudeRegex = /^-?\d{1,2}(\.\d{1,4})?$/;
    if (!latitudeRegex.test(latStr)) {
      alert('Please enter Latitude in format 47.1234 (e.g., 47.1234 or -47.1234)');
      return;
    }
    
    // Validate latitude range (-90.0000 to 90.0000)
    const lat = parseFloat(formData.latitude);
    if (isNaN(lat) || lat < -90 || lat > 90) {
      alert('Latitude must be between -90.0000 and 90.0000');
      return;
    }
    
    // Validate longitude format (e.g., 97.1234)
    const lngStr = formData.longitude.toString().trim();
    const longitudeRegex = /^-?\d{1,3}(\.\d{1,4})?$/;
    if (!longitudeRegex.test(lngStr)) {
      alert('Please enter Longitude in format 97.1234 (e.g., 97.1234 or -97.1234)');
      return;
    }
    
    // Validate longitude range (-180.0000 to 180.0000)
    const lng = parseFloat(formData.longitude);
    if (isNaN(lng) || lng < -180 || lng > 180) {
      alert('Longitude must be between -180.0000 and 180.0000');
      return;
    }
    
    setLoading(true);
    
    try {
      const token = secureStorage.getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      if (editingEntry) {
        await axios.put(`${API}/hira/${editingEntry.id}`, formData, { headers });
      } else {
        await axios.post(`${API}/hira`, formData, { headers });
      }
      onSave();
    } catch (error) {
      console.error('Error saving HIRA entry:', error);
      console.error('Error details:', error.response?.data);
      alert('Error saving HIRA entry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateChangeFrequency = (index, checked) => {
    // Capture scroll position immediately before state update
    scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
    const newArray = [...formData.change_in_frequency];
    newArray[index] = checked;
    setFormData(prev => ({ ...prev, change_in_frequency: newArray }));
  };

  const updateChangeVulnerability = (index, checked) => {
    // Capture scroll position immediately before state update
    scrollPositionRef.current = window.pageYOffset || document.documentElement.scrollTop;
    const newArray = [...formData.change_in_vulnerability];
    newArray[index] = checked;
    setFormData(prev => ({ ...prev, change_in_vulnerability: newArray }));
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" style={{ overflowAnchor: 'auto' }}>
      <div className="mb-6">
        <Button 
          variant="ghost" 
          onClick={onBack}
          className="text-gray-400 hover:text-orange-500 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to HIRA List
        </Button>
        <h1 className="text-3xl font-bold text-orange-500 mb-2">
          {editingEntry ? 'Edit HIRA Entry' : 'Add New HIRA Entry'}
        </h1>
        <p className="text-gray-400">Hazard Identification Risk Assessment for emergency planning</p>
      </div>

      <form onSubmit={handleSubmitLocal} className="space-y-6" style={{ overflowAnchor: 'auto' }}>
        {/* Hazard Image Upload */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Hazard Image</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-6">
              <div
                {...hazardDropProps}
                data-testid="hazard-image-dropzone"
                onClick={() => document.getElementById('hazard-image-upload').click()}
                className={`w-32 h-32 rounded-lg bg-gray-700 border-2 border-dashed flex items-center justify-center overflow-hidden cursor-pointer transition-colors ${
                  isHazardDragging ? 'border-orange-500 bg-orange-500/10 ring-2 ring-orange-500' : 'border-gray-600 hover:border-orange-500/50'
                }`}
                title="Click to browse or drop an image here"
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="Hazard" className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <div className="text-center px-2">
                    <AlertTriangle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <span className="text-xs text-gray-400">{isHazardDragging ? 'Drop to upload' : 'No image · Drop here'}</span>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    id="hazard-image-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('hazard-image-upload').click()}
                    className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10 mr-3"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Photo
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCameraCapture}
                    className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Take Photo
                  </Button>
                </div>
                <p className="text-sm text-gray-400">Upload image of hazard area or related documentation — or drag &amp; drop directly onto the image area</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Basic Information */}
        <Card className="bg-gray-800 border-gray-700">
          <CardHeader>
            <CardTitle className="text-orange-500">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-gray-300">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                required
              />
            </div>

            <div>
              <Label htmlFor="description" className="text-gray-300">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                rows={3}
                required
              />
            </div>

            <div>
              <Label htmlFor="notes" className="text-gray-300">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="bg-gray-700 border-gray-600 text-white"
                rows={2}
              />
            </div>

            <div>
              <Label htmlFor="disaster_type" className="text-gray-300">Disaster Type *</Label>
              <Select value={formData.disaster_type} onValueChange={(value) => setFormData(prev => ({ ...prev, disaster_type: value }))}>
                <SelectTrigger className="bg-gray-700 border-gray-600 text-white">
                  <SelectValue placeholder="Select disaster type" />
                </SelectTrigger>
                <SelectContent className="bg-gray-700 border-gray-600 max-h-60">
                  <SelectItem value="manmade-separator" disabled className="font-semibold text-orange-400">
                    --- MANMADE DISASTERS ---
                  </SelectItem>
                  {disasterTypes.slice(0, NATURAL_START_INDEX).map((type) => (
                    <SelectItem key={type} value={type} className="text-white focus:bg-gray-600">
                      {type}
                    </SelectItem>
                  ))}
                  <SelectItem value="natural-separator" disabled className="font-semibold text-green-400">
                    --- NATURAL DISASTERS ---
                  </SelectItem>
                  {disasterTypes.slice(NATURAL_START_INDEX).map((type) => (
                    <SelectItem key={type} value={type} className="text-white focus:bg-gray-600">
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="latitude" className="text-gray-300">Latitude *</Label>
                <Input
                  id="latitude"
                  type="text"
                  value={formData.latitude}
                  onChange={handleLatitudeChange}
                  onBlur={handleLatitudeBlur}
                  className={`bg-gray-700 border-gray-600 text-white ${validationErrors.latitude ? 'border-red-500' : ''}`}
                  placeholder="47.0000"
                  required
                />
                {validationErrors.latitude && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.latitude}</p>
                )}
              </div>
              <div>
                <Label htmlFor="longitude" className="text-gray-300">Longitude *</Label>
                <Input
                  id="longitude"
                  type="text"
                  value={formData.longitude}
                  onChange={handleLongitudeChange}
                  onBlur={handleLongitudeBlur}
                  className={`bg-gray-700 border-gray-600 text-white ${validationErrors.longitude ? 'border-red-500' : ''}`}
                  placeholder="-97.0000"
                  required
                />
                {validationErrors.longitude && (
                  <p className="text-red-500 text-sm mt-1">{validationErrors.longitude}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Risk Assessment Categories */}
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-orange-500">Risk Assessment Categories</h2>
          
          <RadioSection
            title="Frequency"
            levels={frequencyLevels}
            value={formData.frequency}
            onChange={(value) => setFormData(prev => ({ ...prev, frequency: value }))}
            scrollRef={scrollPositionRef}
          />

          <RadioSection
            title="Fatalities"
            levels={fatalityLevels}
            value={formData.fatalities}
            onChange={(value) => setFormData(prev => ({ ...prev, fatalities: value }))}
            scrollRef={scrollPositionRef}
          />

          <RadioSection
            title="Injuries"
            levels={injuryLevels}
            value={formData.injuries}
            onChange={(value) => setFormData(prev => ({ ...prev, injuries: value }))}
            scrollRef={scrollPositionRef}
          />

          <RadioSection
            title="Evacuation"
            levels={evacuationLevels}
            value={formData.evacuation}
            onChange={(value) => setFormData(prev => ({ ...prev, evacuation: value }))}
            scrollRef={scrollPositionRef}
          />

          <RadioSection
            title="Property Damage"
            levels={propertyLevels}
            value={formData.property_damage}
            onChange={(value) => setFormData(prev => ({ ...prev, property_damage: value }))}
            scrollRef={scrollPositionRef}
          />

          <RadioSection
            title="Critical Infrastructure Service Impact (CI)"
            levels={infrastructureLevels}
            value={formData.critical_infrastructure}
            onChange={(value) => setFormData(prev => ({ ...prev, critical_infrastructure: value }))}
            scrollRef={scrollPositionRef}
          />

          <RadioSection
            title="Environmental Damage"
            levels={environmentalLevels}
            value={formData.environmental_damage}
            onChange={(value) => setFormData(prev => ({ ...prev, environmental_damage: value }))}
            scrollRef={scrollPositionRef}
          />

          <RadioSection
            title="Business Financial Impact"
            levels={businessLevels}
            value={formData.business_financial_impact}
            onChange={(value) => setFormData(prev => ({ ...prev, business_financial_impact: value }))}
            scrollRef={scrollPositionRef}
          />

          <RadioSection
            title="Psychosocial Impact"
            levels={psychosocialLevels}
            value={formData.psychosocial_impact}
            onChange={(value) => setFormData(prev => ({ ...prev, psychosocial_impact: value }))}
            scrollRef={scrollPositionRef}
          />

          {/* Change in Frequency */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-orange-500 flex items-center">
                Change in Frequency <span className="text-red-400 ml-1">*</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {frequencyChangeQuestions.map((question, index) => (
                <div key={`freq-q-${index}`} className="space-y-2">
                  <Label className="text-gray-300">{question}</Label>
                  <div className="flex items-center gap-6 pl-1">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`freq-${index}-yes`}
                        checked={formData.change_in_frequency[index] === true}
                        onCheckedChange={(checked) => updateChangeFrequency(index, checked === true)}
                      />
                      <Label htmlFor={`freq-${index}-yes`} className="text-gray-300 cursor-pointer">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`freq-${index}-no`}
                        checked={formData.change_in_frequency[index] !== true}
                        onCheckedChange={(checked) => updateChangeFrequency(index, !(checked === true))}
                      />
                      <Label htmlFor={`freq-${index}-no`} className="text-gray-300 cursor-pointer">No</Label>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Change in Vulnerability */}
          <Card className="bg-gray-800 border-gray-700">
            <CardHeader>
              <CardTitle className="text-orange-500 flex items-center">
                Change in Vulnerability <span className="text-red-400 ml-1">*</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {vulnerabilityChangeQuestions.map((question, index) => (
                <div key={`vuln-q-${index}`} className="space-y-2">
                  <Label className="text-gray-300">{question}</Label>
                  <div className="flex items-center gap-6 pl-1">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`vuln-${index}-yes`}
                        checked={formData.change_in_vulnerability[index] === true}
                        onCheckedChange={(checked) => updateChangeVulnerability(index, checked === true)}
                      />
                      <Label htmlFor={`vuln-${index}-yes`} className="text-gray-300 cursor-pointer">Yes</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`vuln-${index}-no`}
                        checked={formData.change_in_vulnerability[index] !== true}
                        onCheckedChange={(checked) => updateChangeVulnerability(index, !(checked === true))}
                      />
                      <Label htmlFor={`vuln-${index}-no`} className="text-gray-300 cursor-pointer">No</Label>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>

        {/* Live Risk Score Preview */}
        {(() => {
          const score = calculateHIRARiskScore(formData);
          const lvl = getHIRARiskLevel(formData);
          return (
            <Card className="bg-gray-800 border-orange-500/40" data-testid="hira-risk-score-preview">
              <CardHeader>
                <CardTitle className="text-orange-500">Risk Score Preview</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs uppercase text-gray-400">Impact</div>
                  <div className="text-2xl font-semibold text-white">{score.base}</div>
                  <div className="text-[11px] text-gray-500">sum of frequency + all 8 impact levels</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-400">Trend</div>
                  <div className="text-2xl font-semibold text-white">+{score.trend}</div>
                  <div className="text-[11px] text-gray-500">{score.yesCount} × 1 (Yes answers across 8 change checkboxes)</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-400">Total</div>
                  <div className="text-2xl font-semibold text-white">{score.total}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-gray-400">Risk Level</div>
                  <span className={`inline-block mt-1 px-2 py-1 rounded border text-sm font-semibold ${lvl.color}`}>
                    {lvl.level}
                  </span>
                  <div className="text-[11px] text-gray-500 mt-1">Critical ≥ 32 · High 20-31 · Medium 10-19 · Low &lt; 10</div>
                </div>
              </CardContent>
            </Card>
          );
        })()}

        <div className="flex justify-end space-x-4 pt-6">
          <Button type="button" variant="outline" onClick={onBack} disabled={loading}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            className="bg-orange-500 hover:bg-orange-600 text-black font-semibold"
            disabled={loading}
          >
            {loading ? 'Saving...' : (
              <>
                <Save className="h-4 w-4 mr-2" />
                {editingEntry ? 'Update HIRA Entry' : 'Save HIRA Entry'}
              </>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};


export default HIRAForm;
